import React, { useEffect, useState } from "react"
import Box from "@mui/material/Box"
import Drawer from "@mui/material/Drawer"
import List from "@mui/material/List"
import ListItem from "@mui/material/ListItem"
import ListItemButton from "@mui/material/ListItemButton"
import ListItemIcon from "@mui/material/ListItemIcon"
import ListItemText from "@mui/material/ListItemText"
import { useLocation, useNavigate } from "react-router-dom"
import { Badge, ThemeProvider, createTheme } from "@mui/material"
import ListAltIcon from '@mui/icons-material/ListAlt';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import GradingIcon from '@mui/icons-material/Grading';
import PollIcon from '@mui/icons-material/Poll';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import FeedbackIcon from '@mui/icons-material/Feedback';
import { ReactComponent as LogoutIcon } from "../../assests/images/logout.svg"
import { ReactComponent as UserProfile } from "../../assests/images/userProfile.svg"
import { ReactComponent as EyeOn } from "../../assests/images/eye_on.svg"
import "./sidebar.css"
import { useAuthContext } from "../../context/AuthContext"
import VersionTooltip from "../VersionTooltip/VersionTooltip"

const menuItems = [
  {
    key: 'create-dashboard',
    label: 'Dashboard',
    path: '/create-dashboard',
    icon: QueryStatsIcon,
    activePaths: ['create-dashboard', 'view-dashboard']
  },
  {
    key: 'kpi',
    label: 'KPI',
    path: '/kpi-list',
    icon: ListAltIcon,
    allowedUserType: [1],
    activePaths: ['kpi-list', 'create-kpi', 'view-kpi', 'edit-kpi']
  },
  {
    key: 'view-reports',
    label: "View Reports",
    path: '/kpi-report',
    icon: QueryStatsIcon,
    activePaths: ['kpi-report']
  },
  {
    key: 'generate-scorecard',
    label: "Generate Scorecard",
    path: '/generate-scorecard',
    icon: GradingIcon,
    activePaths: ['generate-scorecard']
  },
  {
    key: 'generate-chart',
    label: "Generate Chart",
    path: '/generate-chart',
    icon: PollIcon,
    activePaths: ['generate-chart', 'view-chart']
  },
  {
    key: 'users',
    label: 'Users',
    path: '/users',
    icon: AccountCircleIcon,
    allowedUserType: [1],
    activePaths: ['users']
  },
  {
    key: 'feedback',
    label: 'Feedback',
    path: '/feedback',
    icon: FeedbackIcon,
    blinkIcon: true,
    activePaths: ['feedback']
  }
];

export default function Sidebar() {
  const nav = useNavigate()
  const location = useLocation()
  const { userLogout, 
    getFeedbackCount, 
    feedbackCount, 
    isImpersonating, 
    stopImpersonation } = useAuthContext()

  let userType: any = null
  if (localStorage.getItem("user")) {
    const userDataString = localStorage.getItem("user") as string;
    const userData = JSON.parse(userDataString)
    userType = userData?.user_type
  }

  const theme = createTheme({
    components: {
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: "#140A9A",
            color: "#FFFFFF",
            width: "220px",
            marginTop: "100px",
            paddingLeft: "12px"
          }
        }
      }
    }
  })

  const handleStopImpersonation = () => {
    stopImpersonation()
      .then(() => console.log("Impersonation stopped successfully"))
      .catch((error: any) => console.error("Failed to stop impersonation:", error));
  };

  const handleLogout = async () => {
    try{
      if (isImpersonating) {
        await stopImpersonation()
        .then(() => console.log("Impersonation stopped successfully"))
        .catch((error: any) => console.error("Failed to stop impersonation:", error));
      }
      await userLogout();
    } catch (error) {
      console.error("Error during logout:", error);
      nav("/login");
    }
  }

  const handleListItemClick = (event: any, path: string) => {
    nav(path)
  }

  const isUserProfileActive = location.pathname === "/user-profile";

  const list = () => (
    <div className="sidebar-wrapper">
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "calc(100vh - 100px)"
        }}
        role="presentation"
      >
        <List>
          {
          menuItems?.filter(
            item => item?.allowedUserType?.length
              ? item?.allowedUserType?.includes(userType) : true
          ).map(item => {
            // const active = location.pathname === item?.path;
            const active = item.activePaths.includes(location.pathname.split('/')[1]);
            const IconComponent = item?.icon;
            return <ListItem
                key={item?.key}
                disablePadding
                className={active ? "selected" : ""}
              >
                <ListItemButton
                  selected={active}
                  onClick={event => handleListItemClick(event, item?.path)}
                >
                  <ListItemIcon sx={{ minWidth: 45 }}>
                    {
                      (item?.blinkIcon && feedbackCount > 0) ?
                        <Badge badgeContent={feedbackCount} color="error">
                          <IconComponent
                            style={{ height: "20px", width: "20px" }}
                            sx={{ color: active ? '#140A9A' : "#FFFFFF" }}
                          />    
                        </Badge>
                        :
                        <IconComponent
                          style={{ height: "20px", width: "20px" }}
                          sx={{ color: active ? '#140A9A' : "#FFFFFF" }}
                        />
                    }
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: "13px" }}
                    primary={item?.label}
                  />
                </ListItemButton>
              </ListItem>
            })
          }
        </List>
        <div className="sidebar-footer">
        {isImpersonating && (
            <ListItem 
              disablePadding
              className={isImpersonating ? "selected" : ""}
              >
              <ListItemButton
                selected={isImpersonating}
                onClick={handleStopImpersonation}
              >
                <ListItemIcon sx={{ minWidth: 45 }}>
                  <EyeOn
                    style={{ height: "24px", width: "24px" }}
                    fill={isImpersonating ? '#140A9A' : "#FFFFFF" }
                  />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ fontSize: "13px" }}
                  primary={"Stop Impersonation"}
                />
              </ListItemButton>
            </ListItem>
          )}
          <ListItem
            disablePadding
            className={isUserProfileActive ? "selected" : ""}
          >
            <ListItemButton
              selected={isUserProfileActive}
              onClick={() => nav("/user-profile")}
            >
              <ListItemIcon sx={{ minWidth: 45 }}>
                <UserProfile
                  style={{ height: "24px", width: "24px" }}
                  fill={isUserProfileActive ? '#140A9A' : "#FFFFFF" }
                />
              </ListItemIcon>
              <ListItemText
                primaryTypographyProps={{ fontSize: "13px" }}
                primary={"User profile"}
              />
            </ListItemButton>
          </ListItem>
          <ListItem key={"Logout"} disablePadding>
            <ListItemButton>
              <ListItemIcon>
                <LogoutIcon style={{ height: "20px", width: "20px" }} />
              </ListItemIcon>
              <ListItemText
                onClick={handleLogout}
                primaryTypographyProps={{ fontSize: "14px" }}
                primary="Logout"
              />
            </ListItemButton>
          </ListItem>
        </div>
      </Box>
      <VersionTooltip style={{ marginBottom: "20px" ,marginLeft: "30px"}} />
    </div>
  )

  useEffect(()=>{
    // Define a function to call getFeedbackCount every 5 minutes
    let intervalId: NodeJS.Timeout | undefined;
    if(location.pathname !== "/login"){
      getFeedbackCount()
      // Set up the interval to call getFeedbackCount every 5 minutes
      intervalId = setInterval(() => {
        getFeedbackCount();
      }, 5 * 60 * 1000); // 5 minutes in milliseconds
    }
    // Clean up the interval when the component unmounts or the location changes
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [location.pathname])

  return (
    <ThemeProvider theme={theme}>
      <div style={{ color: "#2A1E5C" }}>
        <Drawer anchor={"left"} variant="permanent">
          {list()}
        </Drawer>
      </div>
    </ThemeProvider>
  )
}
